import { useCallback, useState } from 'react';

export interface DialogProperties {
  open: boolean;
  onClose?: () => void;
  component?: React.ReactNode;
  containerStyles?: string;
  headerStyles?: string;
  headerElement?: React.ReactNode;
  title?: React.ReactNode;
  errorElement?: React.ReactNode;
}

const DEFAULT_DIALOG_STATE: DialogProperties = { open: false };

const useDialog = (initialState: DialogProperties = DEFAULT_DIALOG_STATE) => {
  const [dialogProperties, setDialogProperties] = useState<DialogProperties>(initialState);

  const closeDialog = useCallback(() => {
    setDialogProperties((previous) => ({ ...previous, open: false }));
  }, []);

  const openDialog = useCallback(
    (component?: React.ReactNode) => {
      setDialogProperties({
        open: true,
        component,
        onClose: closeDialog,
      });
    },
    [closeDialog],
  );

  const updateDialogProperties = useCallback((newProperties: Partial<DialogProperties>) => {
    setDialogProperties((previous) => ({ ...previous, ...newProperties }));
  }, []);

  const onClose = useCallback(() => {
    if (dialogProperties.onClose) {
      dialogProperties.onClose();
    }
    closeDialog();
  }, [closeDialog, dialogProperties]);

  return {
    dialogProperties,
    openDialog,
    closeDialog,
    updateDialogProperties,
    isOpen: dialogProperties.open,
    onClose,
  };
};

export default useDialog;
